import { useEffect, useRef } from 'react'
import { HashLink } from './hash-link.tsx'
import { JoinList } from './join-list.tsx'
import { ReserveSeat } from './reserve-seat.tsx'

export function WorkshopHero() {
	const farRef = useRef<HTMLDivElement>(null)
	const midRef = useRef<HTMLDivElement>(null)
	const nearRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		if (reduce) return

		let frame = 0
		const apply = () => {
			const y = window.scrollY
			const mobile = window.innerWidth < 768
			const far = mobile ? 0.08 : 0.18
			const mid = mobile ? 0.04 : 0.1
			const near = mobile ? -0.02 : -0.06
			if (farRef.current) {
				farRef.current.style.transform = `translate3d(0, ${y * far}px, 0) scale(1.14)`
			}
			if (midRef.current) {
				midRef.current.style.transform = `translate3d(0, ${y * mid}px, 0)`
			}
			if (nearRef.current) {
				nearRef.current.style.transform = `translate3d(0, ${y * near}px, 0)`
			}
		}

		const onScroll = () => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(apply)
		}

		apply()
		window.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('resize', onScroll)
		return () => {
			cancelAnimationFrame(frame)
			window.removeEventListener('scroll', onScroll)
			window.removeEventListener('resize', onScroll)
		}
	}, [])

	return (
		<section className="relative isolate min-h-dvh overflow-hidden bg-navy">
			<div ref={farRef} className="absolute inset-[-14%] will-change-transform">
				<video
					className="h-full w-full object-cover"
					autoPlay
					muted
					loop
					playsInline
					preload="auto"
					poster="/img/workshop/hero-poster.jpg"
				>
					<source src="/img/workshop/hero.mp4" type="video/mp4" />
				</video>
			</div>

			<div
				ref={midRef}
				className="pointer-events-none absolute inset-0 will-change-transform"
				aria-hidden="true"
			>
				<div className="absolute inset-0 bg-navy/50" />
				<div
					className="absolute inset-0 opacity-35 mix-blend-multiply"
					style={{
						backgroundImage: 'url(/img/workshop/paper-grain.jpg)',
						backgroundSize: '420px',
					}}
				/>
				<img
					src="/img/workshop/hero-cutout.png"
					alt=""
					className="absolute bottom-[-8%] right-[-8%] hidden h-[88%] w-auto max-w-[58%] object-contain object-bottom md:block"
				/>
			</div>

			<header className="relative z-20 flex h-16 items-center justify-between px-6 md:h-20 md:px-12">
				<HashLink href="#top" className="flex items-center gap-3 text-cream">
					<img
						src="/img/workshop/mark.png"
						alt=""
						className="h-8 w-8 object-contain"
					/>
					<span className="font-mono text-[11px] uppercase tracking-[0.18em]">
						Your AI
					</span>
				</HashLink>
				<nav className="hidden items-center gap-8 text-sm text-cream/90 md:flex">
					<HashLink
						href="#about"
						className="underline-offset-4 hover:underline"
					>
						About
					</HashLink>
					<HashLink
						href="#workshop"
						className="underline-offset-4 hover:underline"
					>
						Workshop
					</HashLink>
					<HashLink
						href="#andre"
						className="underline-offset-4 hover:underline"
					>
						André
					</HashLink>
					<HashLink
						href="#next"
						className="underline-offset-4 hover:underline"
					>
						Next session
					</HashLink>
				</nav>
				<ReserveSeat />
			</header>

			<div
				ref={nearRef}
				className="relative z-10 flex min-h-[calc(100dvh-5rem)] max-w-6xl flex-col justify-end px-6 pb-16 will-change-transform md:px-12 md:pb-20"
			>
				<h1 className="max-w-xl text-4xl font-semibold leading-none tracking-tighter text-cream md:text-6xl">
					An AI that already knows you.
				</h1>
				<p className="mt-5 max-w-[36ch] text-base leading-relaxed text-cream/85 md:text-lg">
					Sit down and put it to work. Do not brief a stranger first.
				</p>
				<div className="mt-8 flex flex-wrap items-center gap-3">
					<ReserveSeat />
					<JoinList className="border-cream text-cream hover:bg-cream hover:text-navy" />
				</div>
			</div>
		</section>
	)
}
